import { Button, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import PagesContainer from "../../../../components/UI/PagesContainer";
import useFetch from "../../../../hooks/useFetch";
import { ConsultData } from "./ConsultData";
import { getMySchedulesPortal } from "../../../../services/mySchedulesPortal.service";
import { IMySchedulesPortal } from "../../../../models/my-schedule-portal";
import ConfirmDate from "../../NewSchedule/Stepper/ConfirmDate";
import dayjs from "dayjs";
import { useExtensionConfigs } from "../../../../hooks/useExtensionConfigs";
import { Extension } from "../../../../models/intalled-extensions";
import { IOrientationItem, Orientations } from "./Orientations";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MapIcon from "@mui/icons-material/Map";
import { ETasyScheduleTypeCode } from "../../model";
import { getMySchedulesPortalOrientations } from "../../../../services/orientations.service";
import DOMPurify from "dompurify";

interface ILinkAccessScheduleDetailsProps {
  firstGet?: boolean;
}

const TRASNLATION_PATH = "page.schedules.scheduleDetails.";

const LinkAccessScheduleDetails = ({
  firstGet,
}: ILinkAccessScheduleDetailsProps) => {
  const { t } = useTranslation();

  const { identifier } = useParams();

  const [searchParams] = useSearchParams();

  const { fetchExtensionConfig } = useExtensionConfigs();

  const { sendRequest: getMySchedulesPortalRequest, loading: mLoading } =
    useFetch(getMySchedulesPortal);

  const { sendRequest: getOrientationsRequest, loading: iLoading } = useFetch(
    getMySchedulesPortalOrientations,
  );

  const [whatsAppNumberConfig, setWhatsAppNumberConfig] = useState("");

  const [addressConfig, setAddressConfig] = useState("");

  const [orientations, setOrientations] = useState<IOrientationItem[]>([]);

  const [schedule, setSchedule] = useState<IMySchedulesPortal | null>(null);

  const onClickAddToCalendar = () => {
    const startDate = dayjs(schedule?.calendarStartDate);
    const endDate = dayjs(schedule?.calendarEndDate);
    const scheduleType =
      schedule?.tasyScheduleTypeCode?.toString() ===
      ETasyScheduleTypeCode.CONSULTATION
        ? t(TRASNLATION_PATH + "consultation")
        : t(TRASNLATION_PATH + "exam");

    const scheduleDate = startDate.format("DD/MM/YYYY");
    const scheduleTime = startDate.format("HH:mm");

    const event = {
      title: scheduleType,
      startDate: startDate.toISOString().replace(/[-:.]/g, ""),
      endDate: endDate.toISOString().replace(/[-:.]/g, ""),
      description: t(TRASNLATION_PATH + "schedule_description", {
        scheduleType,
        physicianName: schedule?.physicianNickName,
        date: scheduleDate,
        time: scheduleTime,
        interpolation: { escapeValue: false },
      }),
    };

    const icsData = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `SUMMARY:${event.title}`,
      `DTSTART:${event.startDate}`,
      `DTEND:${event.endDate}`,
      `DESCRIPTION:${event.description}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n");

    const blob = new Blob([icsData], { type: "text/calendar;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "calendar.ics");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchMySchedulesPortalRequest = useCallback(async () => {
    const historic = !!searchParams.get("historic");

    const { data, success } = await getMySchedulesPortalRequest({
      identifier,
      historic,
      firstGet,
    });

    if (data && success) {
      const scheduleUpdate = data[0];
      setSchedule(scheduleUpdate);

      const orientationUpdate: IOrientationItem[] = [];

      if (scheduleUpdate.orientation) {
        orientationUpdate.push({
          title: t("page.schedules.scheduleDetails.orientationTitles.schedule"),
          content: DOMPurify.sanitize(scheduleUpdate.orientation),
        });
      }

      const { data: orientationsData, success: orientationsSuccess } =
        await getOrientationsRequest({
          procedureInternalCode: scheduleUpdate.procedureInternalCode,
          insuranceCode: scheduleUpdate.insuranceCode,
        });

      if (orientationsSuccess && orientationsData) {
        const orientationValues = [
          {
            title: t(
              "page.schedules.scheduleDetails.orientationTitles.procedure",
            ),
            content: orientationsData.procedureOrientation,
          },
          {
            title: t(
              "page.schedules.scheduleDetails.orientationTitles.procedureInsurance",
            ),
            content: orientationsData.procedureInsuranceOrientation,
          },
          {
            title: t(
              "page.schedules.scheduleDetails.orientationTitles.insurance",
            ),
            content: orientationsData.insuranceObservation,
          },
        ]
          .filter((item): item is { title: string; content: string } =>
            Boolean(item.content),
          )
          .map((item) => ({
            ...item,
            content: DOMPurify.sanitize(item.content),
          }));

        orientationUpdate.push(...orientationValues);
      }

      setOrientations(orientationUpdate);
    }
  }, [
    getMySchedulesPortalRequest,
    getOrientationsRequest,
    identifier,
    firstGet,
    searchParams,
  ]);

  const fetchWhatsAppNumber = useCallback(async () => {
    const configWhatsAppNumber = await fetchExtensionConfig(
      Extension.MY_SCHEDULES,
      "WhatsAppNumber",
    );

    if (configWhatsAppNumber?.value) {
      setWhatsAppNumberConfig(configWhatsAppNumber.value);
    }
  }, [fetchExtensionConfig]);

  const fetchAddress = useCallback(async () => {
    const configWhatsAppNumber = await fetchExtensionConfig(
      Extension.MY_SCHEDULES,
      "Address",
    );

    if (configWhatsAppNumber?.value) {
      setAddressConfig(configWhatsAppNumber.value);
    }
  }, [fetchExtensionConfig]);

  useEffect(() => {
    fetchMySchedulesPortalRequest();
  }, [fetchMySchedulesPortalRequest]);

  useEffect(() => {
    fetchWhatsAppNumber();
    fetchAddress();
  }, [fetchWhatsAppNumber, fetchAddress]);

  const loading = mLoading || iLoading;

  return (
    <PagesContainer
      title={t(TRASNLATION_PATH + "title")}
      hideBackButton={firstGet}
      loading={loading}
      sx={{
        gap: 2,
      }}
      rowActions={
        <>
          {firstGet && (
            <Button
              component={Link}
              to=".."
              relative="route"
              variant="contained"
            >
              {t(TRASNLATION_PATH + "view_all_schedules")}
            </Button>
          )}

          <Button
            component="a"
            href={`https://wa.me/${whatsAppNumberConfig}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            {t(TRASNLATION_PATH + "schedule")}
          </Button>
        </>
      }
    >
      <Stack height="100%" overflow="auto" justifyContent="space-between">
        {schedule && (
          <Stack gap={1}>
            <ConsultData data={schedule} />

            <ConfirmDate
              calendarStatusCode={schedule.calendarStatusCode}
              startHour={dayjs(schedule.calendarStartDate)}
              duration={schedule.minutesDuration}
              scheduleType={String(schedule.tasyScheduleTypeCode)}
              scheduleDate={schedule.calendarStartDate!}
              roomName={schedule.roomName}
              encounterIdentifier={schedule.encounterIdentifier}
              identifier={Number(identifier)}
            />
          </Stack>
        )}
      </Stack>

      <Stack
        flexDirection={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, md: 0 }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Button
          component="a"
          href={addressConfig}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          sx={{
            width: { xs: "100%", md: "auto" },
            gap: 1,
            textTransform: "none",
            py: 1.5,
          }}
        >
          <MapIcon />

          <Typography>{t(TRASNLATION_PATH + "view_on_map")}</Typography>
        </Button>

        {orientations.length > 0 && (
          <Orientations orientations={orientations} />
        )}

        <Button
          variant="contained"
          onClick={onClickAddToCalendar}
          sx={{
            width: { xs: "100%", md: "auto" },
            gap: 1,
            textTransform: "none",
            py: 1.5,
          }}
        >
          <CalendarMonthIcon />

          <Typography>{t(TRASNLATION_PATH + "add_to_calendar")}</Typography>
        </Button>
      </Stack>
    </PagesContainer>
  );
};

export default LinkAccessScheduleDetails;
